<template>
  <McConfirmation
    id="modal-change-vehicle-driver"
    v-model="isShowChangeVehicleDriverModal"
    centered
    noCloseOnEsc
    noCloseOnBackdrop
    :title="$t('fleetTaskInstant.confirmModal.updateVehicleDriver.title')"
    :ok-title="$t('fleetTaskInstant.confirmModal.updateVehicleDriver.confirmBtn')"
    :cancel-title="$t('global.cancel')"
    :okDisabled="okDisabled || isLoading"
    @handle-ok="handleChangeVehicleDriver"
    @cancel="handleClose"
  >
    <formulateForm ref="formRef" v-model="formValues" class="update-vehicle-driver-form">
      <p>{{ $t('fleetTaskInstant.confirmModal.updateVehicleDriver.content') }}</p>
      <template v-for="(field, idx) in fields">
        <div
          v-if="field.type == 'multiselect-input' && field.key === 'vehicle'"
          :key="'input-' + field.key"
          class="mb-3"
        >
          <MultiSelectInput
            :key="'multiselect' + idx"
            :attrs="field"
            :initialValue="currentVehicle"
            :modelValue.sync="fullObjects[field.key]"
            :disabled="isLoading"
            @update:modelValue="handleInput"
          />
          <McAlert
            v-if="isDataChanged"
            class="my-2"
            variant="info"
            :withIcon="true"
            iconPosition="center"
          >
            <template #label>
              {{ $t('fleetTaskInstant.confirmModal.updateVehicleDriver.alertText.vehicle') }}
            </template>
          </McAlert>
        </div>
        <div
          v-else-if="field.type == 'multiselect-input' && field.key === 'driver'"
          :key="'input-' + field.key"
          class="mb-3"
        >
          <MultiSelectInput
            :key="'multiselect' + idx"
            :attrs="field"
            :initialValue="currentDriver"
            :modelValue.sync="fullObjects[field.key]"
            :disabled="isLoading"
            @update:modelValue="handleInput"
          />
          <McAlert
            v-if="isDataChanged"
            class="my-2"
            variant="info"
            :withIcon="true"
            iconPosition="center"
          >
            <template #label>
              {{ $t('fleetTaskInstant.confirmModal.updateVehicleDriver.alertText.driver') }}
            </template>
          </McAlert>
        </div>
        <template
          v-else-if="field.name === 'reason'"
        >
          <FormulateInput
            v-show="isDataChanged"
            v-bind="field"
            :key="'formulate-' + field.name"
            :disabled="isLoading"
            validation-behavior="value"
            error-behavior="value"
          />
        </template>
      </template>
    </formulateForm>
  </McConfirmation>
</template>
<script>
import { mapGetters, mapState } from 'vuex'
import { cloneDeep, isEqual } from 'lodash'
import { EventBus } from '../lib/event-bus.js'
import { Helper } from '@/mixin/helper.js'

import McConfirmation from '@/components/McConfirmation'
import McAlert from '@/components/McAlert'
import MultiSelectInput from './MultiSelectInput'

import updateVehicleDriverFields from '../lib/fields/update-vehicle-driver-fields'

import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import * as NearestVehicle from '@/services/rest-api/lib/fleet-task-instant/nearest-vehicle'
import * as Driver from '@/services/rest-api/lib/fleet-task-instant/driver'

export default {
  components: {
    McConfirmation,
    MultiSelectInput,
    McAlert,
  },
  mixins: [updateVehicleDriverFields, Helper],
  props: {},
  data() {
    return {
      isShowChangeVehicleDriverModal: false,
      isDataChanged: false,
      isLoading: false,
      initialFormValues: {
        vehicle_id: null,
        driver_id: null,
        reason: "",
      },
      initialFullObjects: {
        vehicle: null,
        driver: null,
      },
      formValues: {
        vehicle_id: null,
        driver_id: null,
        reason: "",
      },
      fullObjects: {
        vehicle: null,
        driver: null,
      },
      isInitialized: {
        vehicle: false,
        driver: false
      },
      mappingSuccessData: {
        'vehicle_id': () => this.$t('fleetTaskInstant.field.licensePlate'),
        'driver_id': () => this.$t('global.field.driver')
      }
    }
  },

  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    ...mapGetters('fleetTaskInstant', [
      'currentVehicle',
      'currentDriver',
    ]),
    ...mapState('fleetTaskInstant', [
      'showResponse',
    ]),
    hasTmsUserGroupAccess() {
      return (
        this.hasAccessAll(['tms-user-group:read']) &&
        this.isSuperUser()
      )
    },
    fields() {
      return this.updateVehicleDriverFields.map((field) => {
        if (field.key == 'vehicle') {
          field.listGetter = this.queryVehicles.get
        } else if (field.key == 'driver') {
          field.listGetter = this.queryDrivers.get
        }

        return field
      })
    },
    queryVehicles() {
      return {
        get: async (filter) => {
          filter = {
            ...filter,
            filterButton: true,
          }
          if (this.currentVehicle) {
            filter = {
              ...filter,
              latitude: this.currentVehicle.latitude,
              longitude: this.currentVehicle.longitude,
            }
          }

          if (this.hasTmsUserGroupAccess && this.showResponse?.user_group?.id != null) {
            filter = {
              ...filter,
              user_group_id: this.showResponse.user_group.id,
            }
          }
          const response = await NearestVehicle.get(filter)
          
          return {
            metadata: {
              total_count: response?.data?.total_count,
              page: 1,
            },
            data: {
              paginated_result: response?.data?.result,
            },
            isPagination: false,
          }
        }
      }
    },
    queryDrivers() {
      return {
        get: async (filter) => {
          filter = {
            ...filter,
            filterButton: true,
          }

          if (this.hasTmsUserGroupAccess && this.showResponse?.user_group?.id != null) {
            filter = {
              ...filter,
              user_group_id: this.showResponse.user_group.id,
            }
          }

          return await Driver.get(filter)
        }
      }
    },
    okDisabled() {
      return !this.isDataChanged || this.$refs.formRef?.hasErrors
    }
  },
  
  watch: {
    formValues: {
      deep: true,
      handler: function(newVal) {
        this.isDataChanged = (
          (newVal.driver_id !== this.initialFormValues.driver_id) || 
          (newVal.vehicle_id && newVal.vehicle_id !== this.initialFormValues.vehicle_id)
        )
      }
    },
    currentVehicle: {
      handler: function() {
        if (!this.fullObjects.vehicle && !this.isInitialized.vehicle) {
          this.$set(this.formValues, 'vehicle_id', this.currentVehicle?.id)
          this.$set(this.fullObjects, 'vehicle', this.currentVehicle)

          this.initialFormValues['vehicle_id'] = cloneDeep(this.formValues['vehicle_id'])
          this.initialFullObjects['vehicle'] = cloneDeep(this.fullObjects['vehicle'])

          this.isInitialized.vehicle = true
        }
      }
    },
    currentDriver: {
      handler: function() {
        if (!this.fullObjects.driver && !this.isInitialized.driver) {
          this.$set(this.formValues, 'driver_id', this.currentDriver?.id)
          this.$set(this.fullObjects, 'driver', this.currentDriver)
          
          this.initialFormValues['driver_id'] = cloneDeep(this.formValues['driver_id'])
          this.initialFullObjects['driver'] = cloneDeep(this.fullObjects['driver'])

          this.isInitialized.driver = true
        }
      }
    },
  },

  methods: {
    // using PascalCase because it will be used by parent component
    HandleOpen() {
      this.isShowChangeVehicleDriverModal = true
    },

    handleClose() {
      this.isShowChangeVehicleDriverModal = false
      this.fullObjects = cloneDeep({
        vehicle: this.initialFullObjects.vehicle,
        driver: this.initialFullObjects.driver
      })
      
      this.formValues = cloneDeep({
        vehicle_id: this.initialFormValues.vehicle_id,
        driver_id: this.initialFormValues.driver_id,
      })
    },

    async handleChangeVehicleDriver(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.isLoading = true
      try {
        const data = this.filterUnchangedData(this.formValues, this.initialFormValues)
        
        let updatedData = data[0]
        if (this.hasTmsUserGroupAccess && this.showResponse?.user_group?.id != null) {
          updatedData = {
            ...updatedData,
            user_group_id: this.showResponse.user_group.id,
          }
        }
        
        const res = await FleetTaskInstant.update(this.$route.params.id, updatedData)
  
        if (res?.errors) {
          this.$mcToast.error(this.$t('global.alert.text.internalServerError'))
          return
        }
  
        let includedMsg = []
  
        for (let key in data[0]) {
          if (this.mappingSuccessData[key]) {
            includedMsg.push(this.mappingSuccessData[key]())
          }
        }
        
        this.$mcToast.success(this.$t("fleetTaskInstant.toast.updateVehicleDriver.success", {
          object: includedMsg.join(` ${this.$t(`global.and`)} `)
        }))
        this.handleClearInput()
        EventBus.$emit('refetch-fleet-task-instant', 'init', 'update-map')
        this.$bvModal.hide('modal-change-vehicle-driver')
      } catch {
        this.$mcToast.error(this.$t('global.alert.text.internalServerError'))
      } finally {
        this.isLoading = false
      }
    },

    handleInput({ attrs, value }) {
      // Set FormValues
      this.$set(this.formValues, `${attrs.key}_id`, value?.[attrs.trackBy])

      // Set full object
      this.$set(this.fullObjects, attrs.key, value)
    },

    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}

      keys.forEach((key) => {
        // if both value is falsy
        if (!oldValue[key] && !newValue[key]) return

        // if oldValue has same value with newValue
        if (oldValue[key] == newValue[key]) return

        // if the oldValue is 'Invalid date' and newValue is null
        if (oldValue[key] === 'Invalid date' && newValue[key] === null) return

        // if it is array
        if (Array.isArray(oldValue[key]) || Array.isArray(newValue[key])) {
          // if oldValue is undefined but the newValue is empty array
          if (!oldValue[key] && newValue[key].length === 0) return

          // matching oldValue and newValue array
          const sortedNewValue = newValue[key]?.length ? newValue[key].sort() : newValue[key]
          const sortedOldValue = oldValue[key] ? oldValue[key].sort() : oldValue[key]

          const isSameArray = isEqual(sortedNewValue, sortedOldValue)

          if (isSameArray) return
        }

        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },

    handleClearInput(error = null){
      this.isShowChangeVehicleDriverModal = false
      this.isDataChanged = false

      if (!error) {
        this.initialFormValues = {
          vehicle_id: null,
          driver_id: null,
          reason: "",
        }
  
        this.initialFullObjects = {
          vehicle: null,
          driver: null,
        }

        this.isInitialized = {
          vehicle: false,
          driver: false
        }
      }

      this.formValues = {
        vehicle_id: null,
        driver_id: null,
        reason: "",
      }
      
      this.fullObjects = {
        vehicle: null,
        driver: null,
      }
    }
  },
}
</script>

<style lang="scss">
.update-vehicle-driver-form {
  .multiselect__option--highlight {
    .icon-outline_pin::before {
      color: white !important;
    }
  }

  .__option-content {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .__distance {
      display: flex;
      align-items: center;
      gap: 2px;

      .mc-icon {
        font-size: 16px;
      }
    }

    .__address-name {
      font-weight: 600;
      margin-bottom: 4px;
    }

    .__address-full-name {
      font-weight: 400;
    }

    .__divider {
      height: 4px;
      width: 4px;
      background-color: $mep-primary-200;
      border-radius: 4px;
    }
  }

  .formulate-input-element {
    textarea {
      height: 80px;
      max-height: 80px;
    }
  }
}
</style>